import { supabase } from './supabase'

/**
 * Supersede active assignment rows of a given type (crew or employee) on a job,
 * then optionally insert a new active row. Preserves history via unassigned_at.
 */
export async function supersedeAndAssign(params: {
  jobId: string
  crewId?: string | null
  employeeId?: string | null
}): Promise<void> {
  const { jobId, crewId, employeeId } = params
  const {
    data: { user },
  } = await supabase.auth.getUser()
  const now = new Date().toISOString()

  if (crewId !== undefined) {
    await supabase
      .from('jobs')
      .update({ assigned_crew_id: crewId || null })
      .eq('id', jobId)

    await supabase
      .from('job_assignments')
      .update({ unassigned_at: now, unassigned_by: user?.id || null })
      .eq('job_id', jobId)
      .not('crew_id', 'is', null)
      .is('unassigned_at', null)

    if (crewId) {
      await supabase.from('job_assignments').insert({
        job_id: jobId,
        crew_id: crewId,
        assigned_by: user?.id || null,
      })
    }
  }

  if (employeeId !== undefined) {
    await supabase
      .from('jobs')
      .update({ assigned_employee_id: employeeId || null })
      .eq('id', jobId)

    await supabase
      .from('job_assignments')
      .update({ unassigned_at: now, unassigned_by: user?.id || null })
      .eq('job_id', jobId)
      .not('employee_id', 'is', null)
      .is('unassigned_at', null)

    if (employeeId) {
      await supabase.from('job_assignments').insert({
        job_id: jobId,
        employee_id: employeeId,
        assigned_by: user?.id || null,
      })
    }
  }
}

/**
 * Append an active assignment without removing others of the same type.
 * Sets jobs.assigned_* only when that primary is currently empty.
 * No-op if the exact crew/employee is already active on the job.
 */
export async function addAssignment(params: {
  jobId: string
  crewId?: string | null
  employeeId?: string | null
}): Promise<{ added: boolean }> {
  const { jobId, crewId, employeeId } = params
  if (!crewId && !employeeId) return { added: false }

  const {
    data: { user },
  } = await supabase.auth.getUser()

  if (crewId) {
    const { data: existing } = await supabase
      .from('job_assignments')
      .select('id')
      .eq('job_id', jobId)
      .eq('crew_id', crewId)
      .is('unassigned_at', null)
      .limit(1)
    if (existing?.length) return { added: false }

    const { data: job } = await supabase
      .from('jobs')
      .select('assigned_crew_id')
      .eq('id', jobId)
      .maybeSingle()

    const { error } = await supabase.from('job_assignments').insert({
      job_id: jobId,
      crew_id: crewId,
      assigned_by: user?.id || null,
    })
    if (error) throw error

    if (!job?.assigned_crew_id) {
      await supabase.from('jobs').update({ assigned_crew_id: crewId }).eq('id', jobId)
    }
    return { added: true }
  }

  if (employeeId) {
    const { data: existing } = await supabase
      .from('job_assignments')
      .select('id')
      .eq('job_id', jobId)
      .eq('employee_id', employeeId)
      .is('unassigned_at', null)
      .limit(1)
    if (existing?.length) return { added: false }

    const { data: job } = await supabase
      .from('jobs')
      .select('assigned_employee_id')
      .eq('id', jobId)
      .maybeSingle()

    const { error } = await supabase.from('job_assignments').insert({
      job_id: jobId,
      employee_id: employeeId,
      assigned_by: user?.id || null,
    })
    if (error) throw error

    if (!job?.assigned_employee_id) {
      await supabase.from('jobs').update({ assigned_employee_id: employeeId }).eq('id', jobId)
    }
    return { added: true }
  }

  return { added: false }
}

/** Soft-unassign a single assignment row; promote oldest peer to primary FK if needed. */
export async function softUnassign(params: {
  assignmentId: string
  jobId: string
  crewId?: string | null
  employeeId?: string | null
}): Promise<void> {
  const { assignmentId, jobId, crewId, employeeId } = params
  const {
    data: { user },
  } = await supabase.auth.getUser()
  const now = new Date().toISOString()

  await supabase
    .from('job_assignments')
    .update({ unassigned_at: now, unassigned_by: user?.id || null })
    .eq('id', assignmentId)

  if (crewId) {
    const { data: remaining } = await supabase
      .from('job_assignments')
      .select('id, crew_id, assigned_at')
      .eq('job_id', jobId)
      .not('crew_id', 'is', null)
      .is('unassigned_at', null)
      .order('assigned_at', { ascending: true })

    const next = remaining?.[0]
    await supabase
      .from('jobs')
      .update({ assigned_crew_id: next?.crew_id || null })
      .eq('id', jobId)
  }
  if (employeeId) {
    const { data: remaining } = await supabase
      .from('job_assignments')
      .select('id, employee_id, assigned_at')
      .eq('job_id', jobId)
      .not('employee_id', 'is', null)
      .is('unassigned_at', null)
      .order('assigned_at', { ascending: true })

    const next = remaining?.[0]
    await supabase
      .from('jobs')
      .update({ assigned_employee_id: next?.employee_id || null })
      .eq('id', jobId)
  }
}

/** True if the job has any active team row or a primary FK assignee. */
export function jobHasAssigneeCoverage(
  job: {
    assigned_crew_id?: string | null
    assigned_employee_id?: string | null
  },
  assignments?: Array<{ id?: string }> | null
): boolean {
  if (assignments && assignments.length > 0) return true
  return !!(job.assigned_crew_id || job.assigned_employee_id)
}

export type AssignmentHistoryRow = {
  id: string
  job_id: string
  crew_id?: string | null
  employee_id?: string | null
  assigned_at?: string | null
  unassigned_at?: string | null
  crew?: { id: string; name: string } | null
  employee?: { id: string; first_name: string; last_name: string } | null
}

export async function fetchAssignmentHistory(jobId: string): Promise<AssignmentHistoryRow[]> {
  const { data, error } = await supabase
    .from('job_assignments')
    .select(
      `
      id, job_id, crew_id, employee_id, assigned_at, unassigned_at,
      crew:crews(id, name),
      employee:employees(id, first_name, last_name)
    `
    )
    .eq('job_id', jobId)
    .order('assigned_at', { ascending: false })

  if (error) throw error
  return (data || []).map((row: any) => ({
    ...row,
    crew: Array.isArray(row.crew) ? row.crew[0] || null : row.crew,
    employee: Array.isArray(row.employee) ? row.employee[0] || null : row.employee,
  }))
}

/** Active (not soft-unassigned) assignment rows for a job. */
export async function fetchActiveAssignments(jobId: string): Promise<AssignmentHistoryRow[]> {
  const rows = await fetchAssignmentHistory(jobId)
  return rows.filter((r) => !r.unassigned_at)
}

/** Format a short label for multiple assignees, e.g. "Alex +2" or "East Side · Alex". */
export function formatAssigneeTeamLabel(
  assignments: Array<{
    crew?: { name?: string } | null
    employee?: { first_name?: string; last_name?: string } | null
  }>
): string {
  const names = assignments
    .map((a) => {
      if (a.crew?.name) return a.crew.name
      if (a.employee) {
        return `${a.employee.first_name || ''} ${a.employee.last_name || ''}`.trim()
      }
      return ''
    })
    .filter(Boolean)
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} · ${names[1]}`
  return `${names[0]} +${names.length - 1}`
}
